import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Mail, Phone, Calendar as CalIcon, Tag, MoreVertical, Search, LayoutGrid, List as ListIcon, Loader2, Trash2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new_lead:          { label: 'New Lead',          color: '#3b82f6', bg: '#dbeafe' },
  contacted:         { label: 'Contacted',         color: '#8b5cf6', bg: '#ede9fe' },
  tour_scheduled:    { label: 'Tour Scheduled',    color: '#f59e0b', bg: '#fef3c7' },
  tour_completed:    { label: 'Tour Completed',    color: '#06b6d4', bg: '#cffafe' },
  enrollment_pending:{ label: 'Enrollment Pending',color: '#ec4899', bg: '#fce7f3' },
  enrolled:          { label: 'Enrolled',          color: '#22c55e', bg: '#dcfce7' },
  lost:              { label: 'Lost',              color: '#ef4444', bg: '#fee2e2' },
};

const SOURCE_OPTIONS = [
  { value: 'website',   label: 'Website' },
  { value: 'care_com',  label: 'Care.com' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'google',    label: 'Google' },
  { value: 'referral',  label: 'Referral' },
  { value: 'manual',    label: 'Manual Entry' },
  { value: 'other',     label: 'Other' },
];

function LeadCard({ lead, accentColor, onClick, onDragStart }) {
  const meta = STATUS_META[lead.status] || STATUS_META.new_lead;
  return (
    <div
      className="dc-lead-card"
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={() => onClick(lead)}
      data-testid={`lead-card-${lead.id}`}
      style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: 12, marginBottom: 10, cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(15,23,42,.04)', transition: 'box-shadow .15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <strong style={{ color: '#0f172a', fontSize: 14 }}>{lead.parent_name || '(unnamed)'}</strong>
        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: meta.bg, color: meta.color, whiteSpace: 'nowrap' }}>
          {(lead.source || 'manual').replace('_', ' ')}
        </span>
      </div>
      {lead.child_name && (
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
          👶 {lead.child_name} {lead.child_age && `(${lead.child_age})`}
        </div>
      )}
      {lead.program_interest && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
          <Tag size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
          {lead.program_interest}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#64748b', marginTop: 6 }}>
        {lead.parent_email && <Mail size={11} />}
        {lead.parent_phone && <Phone size={11} />}
        {lead.desired_start_date && <CalIcon size={11} />}
      </div>
    </div>
  );
}

export default function DaycareLeadsKanban({ tenantId, accentColor = '#FACC15' }) {
  const [pipeline, setPipeline] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // kanban | table
  const [search, setSearch] = useState('');
  const [editLead, setEditLead] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        axios.get(`${API}/daycare/${tenantId}/leads/pipeline`),
        axios.get(`${API}/daycare/${tenantId}/leads/metrics`),
      ]);
      setPipeline(p.data);
      setMetrics(m.data);
    } catch (err) {
      console.error('Lead pipeline error:', err);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDragStart = (e, lead) => {
    e.dataTransfer.setData('lead_id', lead.id);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('lead_id');
    if (!leadId) return;
    try {
      await axios.put(`${API}/daycare/${tenantId}/leads/${leadId}`, { status: newStatus });
      toast.success(`Moved to ${STATUS_META[newStatus]?.label}`);
      fetchAll();
    } catch (err) {
      toast.error('Move failed');
    }
  };

  const openNew = () => {
    setEditLead({});
    setFormData({ status: 'new_lead', source: 'manual' });
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setFormData({ ...lead });
  };

  const handleSave = async () => {
    if (!formData.parent_name) {
      toast.error('Parent name is required');
      return;
    }
    try {
      if (editLead?.id) {
        await axios.put(`${API}/daycare/${tenantId}/leads/${editLead.id}`, formData);
        toast.success('Lead updated');
      } else {
        await axios.post(`${API}/daycare/${tenantId}/leads`, formData);
        toast.success('Lead created');
      }
      setEditLead(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead ${lead.parent_name}?`)) return;
    try {
      await axios.delete(`${API}/daycare/${tenantId}/leads/${lead.id}`);
      toast.success('Deleted');
      setEditLead(null);
      fetchAll();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  if (loading || !pipeline) {
    return <div className="dc-loading"><Loader2 size={24} className="animate-spin" /> Loading leads...</div>;
  }

  const filterLead = (lead) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (lead.parent_name || '').toLowerCase().includes(q) ||
      (lead.child_name || '').toLowerCase().includes(q) ||
      (lead.parent_email || '').toLowerCase().includes(q) ||
      (lead.parent_phone || '').toLowerCase().includes(q)
    );
  };

  return (
    <div className="dc-leads" data-testid="dc-leads-kanban">
      {/* Header / Metrics row */}
      <div className="dc-leads-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <MetricCard label="Total Leads" value={metrics?.total || 0} color="#3b82f6" />
        <MetricCard label="New" value={metrics?.by_status?.new_lead || 0} color="#3b82f6" />
        <MetricCard label="Tours" value={(metrics?.by_status?.tour_scheduled || 0) + (metrics?.by_status?.tour_completed || 0)} color="#f59e0b" />
        <MetricCard label="Enrolled" value={metrics?.by_status?.enrolled || 0} color="#22c55e" />
        <MetricCard label="Conversion" value={`${metrics?.conversion_rate || 0}%`} color="#8b5cf6" />
      </div>

      {/* Toolbar */}
      <div className="dc-module-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setView('kanban')} data-testid="leads-view-kanban" style={viewBtn(view === 'kanban', accentColor)}><LayoutGrid size={14} /> Kanban</button>
          <button onClick={() => setView('table')} data-testid="leads-view-table" style={viewBtn(view === 'table', accentColor)}><ListIcon size={14} /> Table</button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
          <div className="dc-search" style={{ maxWidth: 260 }}>
            <Search size={16} />
            <input type="text" placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} data-testid="leads-search" />
          </div>
          <button className="dc-add-btn" style={{ background: accentColor, color: '#1A2139' }} onClick={openNew} data-testid="leads-add-btn">
            <Plus size={16} /> Add Lead
          </button>
        </div>
      </div>

      {/* Kanban view */}
      {view === 'kanban' && (
        <div className="dc-kanban" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12 }}>
          {pipeline.statuses.map(status => {
            const items = (pipeline.buckets[status] || []).filter(filterLead);
            const meta = STATUS_META[status];
            return (
              <div
                key={status}
                className="dc-kanban-col"
                onDragOver={e => e.preventDefault()}
                onDrop={e => handleDrop(e, status)}
                data-testid={`kanban-col-${status}`}
                style={{
                  flex: '0 0 280px', background: '#f8fafc', borderRadius: 12, padding: 12,
                  borderTop: `3px solid ${meta.color}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <strong style={{ color: meta.color, fontSize: 13 }}>{meta.label}</strong>
                  <span style={{ background: meta.bg, color: meta.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: 20 }}>Drop leads here</div>
                ) : (
                  items.map(l => (
                    <LeadCard key={l.id} lead={l} accentColor={accentColor} onClick={openEdit} onDragStart={handleDragStart} />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Table view */}
      {view === 'table' && (
        <div className="dc-table-wrap">
          <table className="dc-table" data-testid="leads-table">
            <thead>
              <tr>
                <th>Parent</th><th>Child</th><th>Email/Phone</th><th>Program</th><th>Source</th><th>Status</th><th>Created</th><th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pipeline.statuses.flatMap(s => pipeline.buckets[s] || []).filter(filterLead).map(lead => {
                const meta = STATUS_META[lead.status] || STATUS_META.new_lead;
                return (
                  <tr key={lead.id}>
                    <td><strong>{lead.parent_name}</strong></td>
                    <td>{lead.child_name || '—'} {lead.child_age && `(${lead.child_age})`}</td>
                    <td>
                      <div style={{ fontSize: 12 }}>{lead.parent_email}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{lead.parent_phone}</div>
                    </td>
                    <td>{lead.program_interest || '—'}</td>
                    <td><span className="dc-badge" style={{ background: '#f1f5f9', color: '#334155' }}>{(lead.source || '').replace('_', ' ')}</span></td>
                    <td><span className="dc-badge" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span></td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{(lead.created_at || '').split('T')[0]}</td>
                    <td>
                      <div className="dc-row-actions">
                        <button className="dc-icon-btn" onClick={() => openEdit(lead)} title="Edit"><Pencil size={15} /></button>
                        <button className="dc-icon-btn delete" onClick={() => handleDelete(lead)} title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit/New Modal */}
      {editLead !== null && (
        <div className="dc-modal-overlay" onClick={() => setEditLead(null)}>
          <div className="dc-modal" onClick={e => e.stopPropagation()} data-testid="lead-form-modal">
            <div className="dc-modal-header">
              <h3>{editLead.id ? 'Edit Lead' : 'New Lead'}</h3>
              <button onClick={() => setEditLead(null)} className="dc-icon-btn"><X size={18} /></button>
            </div>
            <div className="dc-modal-body">
              <Field label="Parent Name *"><input value={formData.parent_name || ''} onChange={e => setFormData(p => ({ ...p, parent_name: e.target.value }))} placeholder="Parent name" data-testid="lead-parent-name" /></Field>
              <Field label="Email"><input type="email" value={formData.parent_email || ''} onChange={e => setFormData(p => ({ ...p, parent_email: e.target.value }))} placeholder="email@example.com" /></Field>
              <Field label="Phone"><input value={formData.parent_phone || ''} onChange={e => setFormData(p => ({ ...p, parent_phone: e.target.value }))} placeholder="(555) 555-5555" /></Field>
              <Field label="Child Name"><input value={formData.child_name || ''} onChange={e => setFormData(p => ({ ...p, child_name: e.target.value }))} placeholder="Child's name" /></Field>
              <Field label="Child Age"><input value={formData.child_age || ''} onChange={e => setFormData(p => ({ ...p, child_age: e.target.value }))} placeholder="3 years" /></Field>
              <Field label="Program Interest">
                <select value={formData.program_interest || ''} onChange={e => setFormData(p => ({ ...p, program_interest: e.target.value }))}>
                  <option value="">Select...</option>
                  <option>Infant Care</option><option>Toddler Care</option><option>Preschool</option><option>Pre-K</option><option>After School</option>
                </select>
              </Field>
              <Field label="Desired Start Date"><input type="date" value={formData.desired_start_date || ''} onChange={e => setFormData(p => ({ ...p, desired_start_date: e.target.value }))} /></Field>
              <Field label="Source">
                <select value={formData.source || 'manual'} onChange={e => setFormData(p => ({ ...p, source: e.target.value }))} data-testid="lead-source">
                  {SOURCE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={formData.status || 'new_lead'} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))} data-testid="lead-status">
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </Field>
              <Field label="Message / Notes"><textarea rows={3} value={formData.message || ''} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} /></Field>
            </div>
            <div className="dc-modal-footer">
              {editLead?.id && <button className="dc-icon-btn delete" onClick={() => handleDelete(editLead)} title="Delete" style={{ marginRight: 'auto' }}><Trash2 size={15} /></button>}
              <button className="dc-btn-cancel" onClick={() => setEditLead(null)}>Cancel</button>
              <button className="dc-btn-save" style={{ background: accentColor, color: '#1A2139' }} onClick={handleSave} data-testid="lead-save">
                {editLead?.id ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="dc-form-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function viewBtn(active, accent) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    background: active ? accent : '#fff', color: active ? '#1A2139' : '#475569',
    border: active ? `1px solid ${accent}` : '1px solid #e5e7eb',
    cursor: 'pointer',
  };
}
